package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.editor.unavailableSourceReviewMessage
import dev.lingvo.site.components.source.SourceComparison
import dev.lingvo.site.components.source.validCurrentSource
import dev.lingvo.site.components.source.validEarlierSource
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t

internal fun sourceReviewRequired(item: ReviewItemDto, guardedMode: Boolean = false): Boolean =
    guardedMode || Session.serverFeatures.sourceAwareReview || item.sourceSnapshot != null

internal fun reviewSourceClaimAvailable(item: ReviewItemDto, current: TranslationDto? = null, guardedMode: Boolean = false): Boolean {
    if (!sourceReviewRequired(item, guardedMode)) return true
    return Session.serverFeatures.sourceAwareReview && item.validSourceSnapshot() != null &&
        (current == null || current.keyId == item.keyId) &&
        isCanonicalSourceReviewId((current?.revision ?: item.targetRevision).orEmpty())
}

/** Full evidence is displayed before this button can bind a different source or target. */
@Composable
internal fun ReviewSourceConflict(
    change: SourceChangeDto,
    canApprove: Boolean,
    busy: Boolean,
    onApprove: (ReviewItemDto) -> Unit,
    onReload: () -> Unit,
) {
    val source = change.validCurrentSource()
    val target = change.target
    val freshItem = if (source != null && target != null) ReviewItemDto(
        keyId = change.keyId, keyName = change.keyId,
        sourceValue = source.value, sourcePluralForms = source.pluralForms,
        targetValue = target.value, targetPluralForms = target.pluralForms,
        updatedAt = target.updatedAt, sourceSnapshot = source, targetRevision = target.revision,
    ) else null
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LAlert(t("review.review_source_guard.source_or_translation_changed", "Source or translation changed"),
                t("review.review_source_guard.nothing_was_approved_compare_the_complete", "Nothing was approved. Compare the complete current source and saved translation before deciding again."),
                variant = LAlertVariant.WARNING, live = true)
            SourceComparison(source, change.validEarlierSource(), target)
            LButton(t("review.review_source_guard.approve_with_this_source", "Approve with this source"), {
                freshItem?.let(onApprove)
            }, enabled = freshItem != null && reviewApprovalAvailable(freshItem, null, canApprove, busy, false))
            LButton(t("review.review_source_guard.reload_the_queue", "Reload the queue"), onReload,
                variant = LButtonVariant.SECONDARY, enabled = !busy)
            if (freshItem == null || !reviewSourceClaimAvailable(freshItem)) LAlert(
                t("review.review_source_guard.source_aware_review_is_unavailable", "Source-aware review is unavailable"),
                unavailableSourceReviewMessage(), variant = LAlertVariant.INFO)
        }
    }
}
